/**
 * Shared Field Registry Helper Utilities
 * Generic helper functions that work with any field registry
 */

import { BaseFieldDefinition } from '../types/fieldRegistry';

/**
 * Create a set of helper functions for a field registry
 * @param registry - Array of field definitions for a specific domain
 * @returns Object with helper functions for querying the registry
 */
export function createFieldHelpers<TEntity>(
  registry: BaseFieldDefinition<TEntity>[]
) {
  return {
    /**
     * Get a field definition by its key
     */
    getFieldByKey: (key: string): BaseFieldDefinition<TEntity> | undefined => {
      return registry.find(f => f.key === key);
    },

    /**
     * Get all searchable fields (appear in Advanced Search)
     */
    getSearchableFields: (): BaseFieldDefinition<TEntity>[] => {
      return registry.filter(f => f.isSearchable);
    },

    /**
     * Get all filterable fields (can be filtered in DataGrid)
     */
    getFilterableFields: (): BaseFieldDefinition<TEntity>[] => {
      return registry.filter(f => f.isFilterable);
    },

    /**
     * Get all sortable fields (can be sorted in DataGrid)
     */
    getSortableFields: (): BaseFieldDefinition<TEntity>[] => {
      return registry.filter(f => f.isSortable);
    },

    /**
     * Get all editable fields (can be edited in modal)
     * Excludes readonly, computed, and explicitly non-editable fields
     */
    getEditableFields: (): BaseFieldDefinition<TEntity>[] => {
      return registry.filter(
        f => !f.isReadOnly && !f.isComputed && f.isEditable !== false
      );
    },

    /**
     * Get all relation fields
     */
    getRelationFields: (): BaseFieldDefinition<TEntity>[] => {
      return registry.filter(f => f.isRelation && f.relationConfig);
    },

    /**
     * Get all computed fields
     */
    getComputedFields: (): BaseFieldDefinition<TEntity>[] => {
      return registry.filter(f => f.isComputed);
    },

    /**
     * Get all readonly fields
     */
    getReadOnlyFields: (): BaseFieldDefinition<TEntity>[] => {
      return registry.filter(f => f.isReadOnly);
    },

    /**
     * Get all required fields
     */
    getRequiredFields: (): BaseFieldDefinition<TEntity>[] => {
      return registry.filter(f => f.isRequired);
    },
  };
}
